import { Box, Container } from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { MeetingCallPanel } from "../../components/Meeting/MeetingCallPanel";
import { ConnectionStatus } from "../../components/Meeting/opentok";
import { Locale } from "../../core/api/model";
import { useCurrentUser } from "../../core/hooks/user";
import SpinnerModal from "../../core/spinner-modal";
import { useUpdateMeetingMutation } from "../../generated/platform";
import {
  useMeeting,
  useRequestChatToken,
  useViewAs,
  ViewAs,
} from "../hooks/meeting";

interface Props {
  locale: Locale;
  showErrorModal: (title: string, description: string, raw?: any) => void;
}

export const MeetingCall = ({ locale, showErrorModal }: Props) => {
  const { uuid } = useParams<{ uuid: string }>();
  const intl = useIntl();
  const history = useHistory();
  const showError = useCallback(
    (raw?: any) => {
      showErrorModal(
        intl.formatMessage({
          id: "MeetingCallError",
          defaultMessage: "Termin fehlgeschlagen",
        }),
        intl.formatMessage({
          id: "MeetingCallErrorText",
          defaultMessage: "Leider konnte der Termin nicht gestartet werden.",
        }),
        raw
      );
    },
    [intl, showErrorModal]
  );
  const { user, loading: userLoading } = useCurrentUser(showError);
  const { meeting, loading: meetingLoading } = useMeeting(uuid, showError);
  const { request: tokenRequest, loading: tokenLoading } = useRequestChatToken(
    showError
  );

  const [token, setToken] = useState<string | undefined>();
  const [updateMeetingMutation] = useUpdateMeetingMutation();
  const [loading, setLoading] = useState(true);
  const viewAs = useViewAs(user, meeting);

  useEffect(() => {
    if (meeting?.hasEnded) {
      history.push(`/virtual-meet/ended/${meeting.uuid}`);
    }
  }, [meeting, history]);

  useEffect(() => {
    setLoading(tokenLoading || userLoading || meetingLoading);
  }, [tokenLoading, meetingLoading, userLoading]);

  useEffect(() => {
    if (token || loading || !user?.partId || !meeting) {
      return;
    }
    if (
      viewAs in [ViewAs.ActiveParticipant, ViewAs.Expert, ViewAs.Participant]
    ) {
      tokenRequest(user.partId, meeting.uuid)
        .then((t) => {
          if (t.data?.requestChatToken) setToken(t.data.requestChatToken);
        })
        .catch(showError);
    }
  }, [
    viewAs,
    user,
    meeting,
    tokenRequest,
    token,
    setToken,
    loading,
    showError,
  ]);

  return (
    <Box
      component={"main"}
      className="main-background"
      pb={{ xs: 7.5, md: 10 }}
      pt={0.01}
    >
      <Container maxWidth="lg">
        <SpinnerModal active={loading} />
        {token &&
          viewAs !== ViewAs.Forbidden &&
          process.env.REACT_APP_VONAGE_KEY &&
          meeting?.expert &&
          meeting?.product &&
          meeting?.participant &&
          meeting?.chat?.sessionId && (
            <MeetingCallPanel
              viewAs={viewAs}
              locale={locale}
              corporation={{
                //@ts-ignore
                corpName: meeting.corporation.corpName || {},
              }}
              participant={{
                fullName: `${meeting.participant.partName} ${meeting.participant.partSurname}`,
                title: `${meeting.participant.partTitle}`,
                position: `${meeting.participant.partProfPosition}`,
                //@ts-ignore
                corpName: meeting.participant.corporation[0]?.corpName || {},
              }}
              expert={{
                fullName: `${meeting.expert.partName} ${meeting.expert.partSurname}`,
                title: `${meeting.expert.partTitle}`,
                position: `${meeting.expert.partProfPosition}`,
              }}
              date={{
                startDate: new Date(meeting.start),
                endDate: new Date(meeting.end),
              }}
              //@ts-ignore
              productName={meeting.product.classificationTitle || {}}
              //@ts-ignore
              productDescription={meeting.product.classificationDesc || {}}
              onError={showError}
              sessionId={meeting.chat.sessionId}
              apiKey={process.env.REACT_APP_VONAGE_KEY}
              token={token}
              publish={viewAs in [ViewAs.Expert, ViewAs.ActiveParticipant]}
              timeout={5}
              createdAt={meeting.start}
              onStatusChange={(s) => {
                if (s === ConnectionStatus.SubscriptionTimeout) {
                  if (viewAs in [ViewAs.Expert, ViewAs.ActiveParticipant]) {
                    updateMeetingMutation({
                      variables: {
                        id: meeting.id,
                        input: {
                          hasEnded: true,
                          status: "omitted",
                        },
                      },
                    })
                      .then((_) =>
                        history.push(`/virtual-meet/cancel/${meeting.uuid}`)
                      )
                      .catch(showError);
                    return;
                  }
                  history.push(`/virtual-meet/cancel/${meeting.uuid}`);
                }
              }}
              onClose={() => {
                if (viewAs in [ViewAs.Expert, ViewAs.ActiveParticipant]) {
                  updateMeetingMutation({
                    variables: {
                      id: meeting.id,
                      input: {
                        hasEnded: true,
                        status: "ended",
                      },
                    },
                  })
                    .then((_) =>
                      history.push(`/virtual-meet/ended/${meeting.uuid}`)
                    )
                    .catch(showError);
                  return;
                }
                history.push(`/virtual-meet/ended/${meeting.uuid}`);
              }}
            />
          )}
      </Container>
    </Box>
  );
};
